import { AudioLines } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  ACCENT_FAMILIES,
  FIDELITY_STAGES,
  FIDELITY_STATUSES,
  accentLabel,
  receiptStatusLabel,
  stageLabel,
} from "../echoflicks/voiceFidelity";
import type { AccentFidelityUsage } from "./accentFidelityUsage";

const STATUS_HEADERS = {
  accepted: "Accepted",
  rejected: "Rejected",
  uncertain: "Uncertain",
  unsupported: "Unsupported",
  unavailable: "Evaluator unavailable",
} as const;

/**
 * Recent accent-fidelity receipts across customer Echoes (VAF-12). Counts
 * only; internal/no-delivery canaries are already excluded by the API.
 */
export function AccentFidelityUsagePanel({
  usage,
}: {
  usage: AccentFidelityUsage | null | undefined;
}) {
  if (!usage) return null;
  const days = usage.windowDays ?? 14;
  // Most requested first; ties keep the alphabetical family order.
  const requested = ACCENT_FAMILIES.filter(
    (accent) => usage.byRequestedAccent[accent] > 0,
  ).sort(
    (left, right) =>
      usage.byRequestedAccent[right] - usage.byRequestedAccent[left],
  );
  const hasEvidence =
    usage.sessionsWithRequirement > 0 ||
    usage.evidenceWithoutRequirementCount > 0;

  return (
    <Card data-testid="accent-fidelity-usage">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AudioLines className="size-4" aria-hidden="true" />
          Accent fidelity · last {days} days
        </CardTitle>
        <CardDescription>
          What an automated acoustic evaluator reported for customer Echoes that
          asked for an accent. Not human listening and not delivery; only the
          final encoded-audio receipt counts as a verdict.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {usage.status === "unavailable" ? (
          <p className="text-muted-foreground">
            Accent evidence could not be read right now. This is not the same as
            zero — try Refresh in a moment.
          </p>
        ) : !hasEvidence ? (
          <p className="text-muted-foreground">
            No customer Echo asked for an accent in this window.
          </p>
        ) : (
          <>
            <p>
              {usage.sessionsWithRequirement} Echo
              {usage.sessionsWithRequirement === 1 ? "" : "es"} asked for an
              accent
              {requested.length > 0
                ? `: ${requested
                    .map(
                      (accent) =>
                        `${accentLabel(accent)} ${usage.byRequestedAccent[accent]}`,
                    )
                    .join(" · ")}`
                : ""}
              .
            </p>
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Final audio verdicts
              </div>
              <div className="flex flex-wrap gap-2">
                {FIDELITY_STATUSES.map((status) => (
                  <Badge key={status} variant="outline" className="font-normal">
                    {receiptStatusLabel(status)} · {usage.finalVerdicts[status]}
                  </Badge>
                ))}
                <Badge
                  variant="outline"
                  className="border-amber-300 font-normal text-amber-700 dark:text-amber-400"
                >
                  Unverified — no final audio verdict · {usage.unverifiedCount}
                </Badge>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Receipts by stage (includes retries)
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stage</TableHead>
                    {FIDELITY_STATUSES.map((status) => (
                      <TableHead key={status} className="text-right">
                        {STATUS_HEADERS[status]}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {FIDELITY_STAGES.map((stage) => (
                    <TableRow key={stage}>
                      <TableCell>{stageLabel(stage)}</TableCell>
                      {FIDELITY_STATUSES.map((status) => (
                        <TableCell
                          key={status}
                          className="text-right tabular-nums"
                        >
                          {usage.receiptsByStage[stage][status]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground">
              Policy mode: observe {usage.byPolicyMode.observe} · enforce{" "}
              {usage.byPolicyMode.enforce} · off {usage.byPolicyMode.off} · not
              reported {usage.byPolicyMode.unknown}
              {usage.evidenceWithoutRequirementCount > 0
                ? ` · ${usage.evidenceWithoutRequirementCount} with receipts but no readable accent requirement (not counted above)`
                : ""}
            </p>
          </>
        )}
        <p className="text-xs text-muted-foreground">
          Internal and test canary Echoes excluded:{" "}
          {usage.excludedInternalCount}.
          {usage.truncated
            ? " Only the most recent sessions in the window were read, so counts may be incomplete."
            : ""}
        </p>
      </CardContent>
    </Card>
  );
}
